import { useMemo } from 'react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { NumberFormatter } from '@mantine/core'
import { tableDefaultAlertBannerProps, tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { getStorageCentreLabel } from '@shared/constants/storage-centres'

export type ReceivalHistoryData = {
  dateReceived?: string | null
  purchaseAccountCode?: string | null
  storageCentreCode?: string | null
  totalBalesReceived?: string | null
}

interface ReceivalHistoryTableProps {
  data: ReceivalHistoryData[]
  includePurchaseAccountCode?: boolean
  sumOfTotalBalesReceived?: number
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
}

export const ReceivalHistoryTable = ({
  data = [],
  includePurchaseAccountCode = false,
  sumOfTotalBalesReceived,
  noDataMessage = 'No receival history available for this station',
  isLoading,
  isError,
}: ReceivalHistoryTableProps) => {
  const columns = useMemo<MRT_ColumnDef<ReceivalHistoryData>[]>(() => {
    const baseColumns: MRT_ColumnDef<ReceivalHistoryData>[] = [
      {
        id: 'dateReceived',
        header: 'Date Received',
        accessorFn: (row) => (row.dateReceived == null ? null : new Date(row.dateReceived)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
        Footer: () => <>Totals</>,
      },
      {
        id: 'storageCentreCode',
        accessorKey: 'storageCentreCode',
        header: 'Centre',
        Cell: ({ cell }) => getStorageCentreLabel(cell.getValue<string>()),
      },
      {
        id: 'totalBalesReceived',
        accessorKey: 'totalBalesReceived',
        header: 'Bales',
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        Footer: () => (
          <NumberFormatter
            value={sumOfTotalBalesReceived ?? ''}
            thousandSeparator
            decimalScale={2}
          />
        ),
      },
    ]

    if (includePurchaseAccountCode) {
      baseColumns.splice(1, 0, {
        id: 'purchaseAccountCode',
        accessorKey: 'purchaseAccountCode',
        header: 'Purchase Account',
      })
    }

    return baseColumns
  }, [includePurchaseAccountCode, sumOfTotalBalesReceived])

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    state: {
      isLoading,
      showAlertBanner: isError,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Receival History',
    },
  })

  return (
    <MantineReactTable
      table={table}
      key={includePurchaseAccountCode ? 'with-purchase-account' : 'no-purchase-account'}
    />
  )
}
